// app/javascript/controllers/crud/selecao_dinamica_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "template"]
  static values = {
    colecao: Array, // esperado: [[desc, id, valor], ...]
    nome: String,
    valorVemDe: String,
    totalItems: Number,
    idsSelecionados: Array, // pode vir como [id1,id2...] OU [[id1],[id2]] OU [[id1, ...], ...]
  }

  connect() {
    this.bootstrapExisting()
    this.updateValueForIndex(0) // base
  }

  add(e) {
    e.preventDefault()
    const fs = this.addFieldset()
    if (fs) this.updateValueInside(fs)
  }

  remove(e) {
    e.preventDefault()
    const fieldset = e.target.closest("fieldset")
    if (fieldset) fieldset.remove()
  }

  update(e) {
    const fieldset = e.target.closest("fieldset")
    if (!fieldset) return
    this.updateValueInside(fieldset)
  }

  // -------------------------
  // Bootstrap dos itens vindos do backend
  // -------------------------
  bootstrapExisting() {
    const total = Number(this.totalItemsValue || 1)
    const idsRaw = this.idsSelecionadosValue || []

    if (total <= 1) return

    // normaliza ids para array de ids (strings), independente do formato
    // aceita:
    // - ["10","11"]
    // - [[10],[11]]
    // - [[10, "x"], [11, "y"]]
    const ids = idsRaw.map((v) => {
      if (Array.isArray(v)) return v[0]
      return v
    })

    for (let i = 1; i < total; i++) {
      const fs = this.addFieldset({ silent: true })
      if (!fs) continue

      const select = this.findSelectInFieldset(fs)
      const idToSet = ids[i]

      if (select && idToSet != null && String(idToSet).trim() !== "") {
        select.value = String(idToSet)
        this.updateValueInside(fs)
      }
    }
  }

  // -------------------------
  // Criação de novo fieldset
  // -------------------------
  addFieldset({ silent = false } = {}) {
    const idx = this.containerTarget.querySelectorAll("fieldset").length

    const fragment = this.templateTarget.content.cloneNode(true)
    const fieldset = fragment.querySelector("fieldset")
    if (!fieldset) return null

    const select = fieldset.querySelector("select")
    const input = fieldset.querySelector('input[type="text"]')
    if (!select || !input) return null

    const baseSelect = this.nomeValue
    const baseText = `${this.valorVemDeValue}_${this.nomeValue}`

    const selectId = `${baseSelect}_${idx}`
    const textId = `${baseText}_${idx}`

    // nomes e ids para o submit em params
    select.id = selectId
    select.name = `params[${selectId}]`

    input.id = textId
    input.name = `params[${textId}]`
    input.value = ""

    this.containerTarget.appendChild(fragment)

    // pega referência real já inserida
    const inserted = this.containerTarget.querySelector(`select#${CSS.escape(selectId)}`)?.closest("fieldset")
    if (!silent && inserted) this.updateValueInside(inserted)

    return inserted
  }

  // -------------------------
  // Helpers robustos (SimpleForm-safe)
  // -------------------------
  updateValueForIndex(idx) {
    const baseSelect = this.nomeValue
    const selectIdCandidates = idx === 0
      ? [baseSelect, `params_${baseSelect}`] // cobre casos com/sem prefixo
      : [`${baseSelect}_${idx}`, `params_${baseSelect}_${idx}`]

    const select =
      selectIdCandidates
        .map((id) => this.containerTarget.querySelector(`select#${CSS.escape(id)}`))
        .find(Boolean)

    const fs = select?.closest("fieldset")
    if (fs) this.updateValueInside(fs)
  }

  findSelectInFieldset(fieldset) {
    const base = this.nomeValue
    return (
      fieldset.querySelector(`select#${CSS.escape(base)}`) ||
      fieldset.querySelector(`select#${CSS.escape(`params_${base}`)}`) ||
      fieldset.querySelector("select")
    )
  }

  findValueInputInFieldset(fieldset, idxGuess = null) {
    const baseText = `${this.valorVemDeValue}_${this.nomeValue}`

    const candidates = []
    candidates.push(baseText, `params_${baseText}`)

    if (idxGuess != null) {
      candidates.push(`${baseText}_${idxGuess}`, `params_${baseText}_${idxGuess}`)
    }

    for (const id of candidates) {
      const el = fieldset.querySelector(`input#${CSS.escape(id)}`)
      if (el) return el
    }

    const byPrefix =
      fieldset.querySelector(`input[id^="${baseText}"]`) ||
      fieldset.querySelector(`input[id^="params_${baseText}"]`)

    return byPrefix || fieldset.querySelector('input[type="text"]')
  }

  normalizeMoneyInputValue(v) {
    if (v == null) return ""
    const s = String(v).trim()
    if (!s) return ""

    // "16.5" -> "16,5" (mask_controller completa no blur)
    if (/^\d+(\.\d+)?$/.test(s)) return s.replace(".", ",")
    return s
  }

  // -------------------------
  // Atualiza valor (core)
  // -------------------------
  updateValueInside(fieldset) {
    const select = this.findSelectInFieldset(fieldset)
    if (!select) return

    const m = String(select.id || "").match(/_(\d+)$/)
    const idxGuess = m ? Number(m[1]) : null

    const input = this.findValueInputInFieldset(fieldset, idxGuess)
    if (!input) return

    const selecionado = String(select.value || "")
    const dados = this.colecaoValue || []

    let val = ""
    for (let i = 0; i < dados.length; i++) {
      const item = dados[i] // [descricao, id, valor]
      if (!Array.isArray(item)) continue
      if (String(item[1]) === selecionado) {
        val = item[2] ?? ""
        break
      }
    }

    input.value = this.normalizeMoneyInputValue(val)

    // força qualquer máscara/formatador a reagir
    try { input.dispatchEvent(new Event("input", { bubbles: true })) } catch (_) { }
    try { input.dispatchEvent(new Event("blur", { bubbles: true })) } catch (_) { }
  }
}
